const byName = (a, b) =>
  String(a?.name || "").localeCompare(String(b?.name || ""));

const byLabel = (a, b) =>
  String(a?.label || "").localeCompare(String(b?.label || ""));

export const groupTags = (tags = [], groups = [], search = "") => {
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const filteredTags = (Array.isArray(tags) ? tags : [])
    .filter((tag) =>
      String(tag?.name || "").toLowerCase().includes(normalizedSearch),
    )
    .slice()
    .sort(byName);
  const groupedTagIds = new Set();

  const sections = (Array.isArray(groups) ? groups : [])
    .slice()
    .sort(byLabel)
    .map((group) => {
      const groupIds = new Set(
        (group.tags || [])
          .filter((tag) => tag?.id !== null && tag?.id !== undefined)
          .map((tag) => String(tag.id)),
      );
      groupIds.forEach((id) => groupedTagIds.add(id));

      return {
        id: group.id,
        label: group.label,
        tags: filteredTags.filter((tag) => groupIds.has(String(tag.id))),
      };
    })
    .filter((section) => section.tags.length > 0);

  const ungrouped = filteredTags.filter(
    (tag) => !groupedTagIds.has(String(tag.id)),
  );

  if (ungrouped.length > 0) {
    sections.push({ id: "ungrouped", label: "Ungrouped", tags: ungrouped });
  }

  return sections;
};

export const tagOptionsByGroup = (
  tags = [],
  groups = [],
  valueForTag = (tag) => tag.id,
) =>
  groupTags(tags, groups).flatMap((section) =>
    section.tags.map((tag) => ({
      value: valueForTag(tag),
      label: tag.name,
      group: section.label,
    })),
  );

export const tagFilterOptions = (
  tags = [],
  groups = [],
  groupId,
  valueForTag = (tag) => tag.id,
) => {
  const normalizedGroups = (Array.isArray(groups) ? groups : [])
    .slice()
    .sort(byLabel);
  const selectedGroup = normalizedGroups.find(
    (group) => String(group.id) === String(groupId),
  );
  const selectedTagIds = selectedGroup
    ? new Set(
        (selectedGroup.tags || [])
          .filter((tag) => tag?.id !== null && tag?.id !== undefined)
          .map((tag) => String(tag.id)),
      )
    : null;
  const groupLabelsByTagId = new Map();

  normalizedGroups.forEach((group) => {
    (group.tags || []).forEach((tag) => {
      if (tag?.id === null || tag?.id === undefined) return;

      const tagId = String(tag.id);
      const labels = groupLabelsByTagId.get(tagId) || [];
      if (!labels.includes(group.label)) labels.push(group.label);
      groupLabelsByTagId.set(tagId, labels);
    });
  });

  const uniqueTags = new Map();
  (Array.isArray(tags) ? tags : []).forEach((tag) => {
    if (tag?.id === null || tag?.id === undefined) return;

    const tagId = String(tag.id);
    if ((!selectedTagIds || selectedTagIds.has(tagId)) && !uniqueTags.has(tagId)) {
      uniqueTags.set(tagId, tag);
    }
  });

  return Array.from(uniqueTags.values())
    .sort(byName)
    .map((tag) => {
      const groupLabels = groupLabelsByTagId.get(String(tag.id)) || [];

      return {
        value: valueForTag(tag),
        label: tag.name,
        ...(!selectedGroup && {
          meta: groupLabels.length > 0 ? groupLabels.join(", ") : "Ungrouped",
        }),
      };
    });
};

export const tagsMatchGroup = (tags = [], groups = [], groupId) => {
  if (!groupId || groupId === "all") return true;

  const group = (Array.isArray(groups) ? groups : []).find(
    (candidate) => String(candidate.id) === String(groupId),
  );
  if (!group) return false;

  const groupTagIds = new Set(
    (group.tags || [])
      .filter((tag) => tag?.id !== null && tag?.id !== undefined)
      .map((tag) => String(tag.id)),
  );

  return (Array.isArray(tags) ? tags : []).some((tag) => {
    const tagId = tag?.id ?? tag;
    return tagId !== null && tagId !== undefined && groupTagIds.has(String(tagId));
  });
};
